'use client';
import React, { useState, useRef } from 'react';
import { Download, FileText, BookOpen, Loader2 } from 'lucide-react';
import { Worksheet, getCategoryMeta } from '@/lib/worksheetData';
import { worksheetService } from '@/lib/supabase/worksheetService';
import { toast } from 'sonner';

interface WorksheetCardProps {
  worksheet: Worksheet & { file_path?: string };
}

const SAMPLE_PROBLEMS: Record<string, string[]> = {
  Addition: ['12 + 34 = ___', '47 + 28 = ___', '156 + 89 = ___', '203 + 77 = ___'],
  Subtraction: ['85 − 43 = ___', '100 − 37 = ___', '234 − 98 = ___', '500 − 126 = ___'],
  Multiplication: ['6 × 7 = ___', '8 × 9 = ___', '12 × 4 = ___', '15 × 6 = ___'],
  Division: ['48 ÷ 6 = ___', '72 ÷ 8 = ___', '125 ÷ 5 = ___', '144 ÷ 12 = ___'],
  Fractions: ['1/2 + 1/4 = ___', '3/4 − 1/8 = ___', '2/3 × 3 = ___', '5/6 + 1/3 = ___'],
  Geometry: ['Area = l × w', 'P = 2(l + w)', 'A = πr²', 'V = l × w × h'],
  Algebra: ['x + 5 = 12', '3y = 21', '2x − 4 = 10', 'n/3 = 7'],
  'Word Problems': [
    'Tom has 24 apples…',
    'A train travels at…',
    'Sara saved $15 each…',
    'A garden is 8m wide…',
  ],
};

function WorksheetPreviewTooltip({ ws, catMeta }: { ws: Worksheet; catMeta: ReturnType<typeof getCategoryMeta> }) {
  const problems = SAMPLE_PROBLEMS[ws.category] ?? SAMPLE_PROBLEMS['Addition'];

  return (
    <div className="w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden text-left pointer-events-none">
      {/* Header */}
      <div className={`px-4 py-3 ${catMeta.color}`}>
        <div className="flex items-center gap-2 mb-1">
          <FileText size={14} className={catMeta.textColor} />
          <span className={`text-xs font-bold uppercase tracking-wide ${catMeta.textColor}`}>
            {ws.category}
          </span>
          <span className="ml-auto text-xs font-medium text-gray-500 bg-white/60 rounded px-1.5 py-0.5">
            {ws.grade}
          </span>
        </div>
        <h4 className="text-sm font-bold text-gray-800 leading-tight">{ws.title}</h4>
      </div>

      {/* Mock worksheet body */}
      <div className="px-4 py-3 bg-white">
        <p className="text-[11px] text-gray-500 mb-3 leading-relaxed">{ws.description}</p>

        {/* Ruled lines with sample problems */}
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
            Sample Problems
          </p>
          {problems.map((problem, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-[10px] text-gray-400 w-4 text-right flex-shrink-0">{i + 1}.</span>
              <div className="flex-1 border-b border-dashed border-gray-200 pb-0.5">
                <span className="text-[11px] text-gray-700 font-mono">{problem}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Footer info */}
        <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <BookOpen size={10} />
            {ws.pages} {ws.pages === 1 ? 'page' : 'pages'}
          </span>
          <span>{ws.fileSize}</span>
          <span className="flex items-center gap-1">
            <Download size={10} />
            {ws.downloads.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function WorksheetCard({ worksheet: ws }: WorksheetCardProps) {
  const [downloading, setDownloading] = useState(false);
  const [downloadCount, setDownloadCount] = useState(ws.downloads);
  const [showTooltip, setShowTooltip] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const catMeta = getCategoryMeta(ws.category);

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      if (ws.file_path) {
        const publicUrl = worksheetService.getPublicUrl(ws.file_path);
        const link = document.createElement('a');
        link.href = publicUrl;
        link.download = ws.title + '.pdf';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Increment download count in background
        worksheetService.incrementDownload(ws.id).catch(() => {});
        setDownloadCount((prev) => prev + 1);
        toast.success(`Downloading "${ws.title}"`, {
          description: 'Your PDF is ready to print.',
        });
      } else {
        toast.info(`"${ws.title}" — file not available yet.`);
      }
    } catch {
      toast.error('Download failed', { description: 'Please try again.' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <article
      ref={cardRef}
      className="card-base flex flex-col hover:card-shadow-md transition-all duration-200 hover:-translate-y-0.5 group relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Tooltip Preview */}
      {showTooltip && (
        <div
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-3 animate-in fade-in-0 zoom-in-95 duration-150"
          style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.15))' }}
        >
          <WorksheetPreviewTooltip ws={ws} catMeta={catMeta} />
        </div>
      )}

      {/* Card Header */}
      <div className="p-4 pb-3 flex-1">
        {/* Category + Grade row */}
        <div className="flex items-center justify-between mb-3">
          <span className={`badge-base ${catMeta.color} ${catMeta.textColor}`}>
            {ws.category}
          </span>
          <span className="badge-base bg-muted text-muted-foreground">{ws.grade}</span>
        </div>

        {/* Icon + Title */}
        <div className="flex gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
            <FileText size={18} className="text-primary" />
          </div>
          <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-2 group-hover:text-primary transition-colors duration-150">
            {ws.title}
          </h3>
        </div>

        {/* Description */}
        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {ws.description}
        </p>
      </div>

      {/* Meta row */}
      <div className="px-4 py-2.5 border-t border-border bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <BookOpen size={12} />
          {ws.pages} {ws.pages === 1 ? 'page' : 'pages'}
        </span>
        <span className="flex items-center gap-1 tabular-nums">
          <Download size={12} />
          {downloadCount.toLocaleString()} downloads
        </span>
        <span>{ws.fileSize}</span>
      </div>

      {/* Download Button */}
      <div className="p-3 pt-2.5">
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="w-full btn-primary justify-center scale-press"
          aria-label={`Download ${ws.title}`}
        >
          {downloading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Preparing PDF…
            </>
          ) : (
            <>
              <Download size={14} />
              Download PDF
            </>
          )}
        </button>
      </div>
    </article>
  );
}